import { Component, OnDestroy, OnInit } from '@angular/core';
import { Currency, HxStoreService, isoDate, SaveStoreRequest, StoreFullModel, StoreWorkTimeModel, UiLabel } from 'hx-services';

/** The place of the store in the org tree goes with the request of a new store (`parentId`); an existing store is moved in «Доступы». */
type SaveStoreWithPlace = SaveStoreRequest & { parentId?: number };

interface OutletView { id: number; title: string }
import { ActivatedRoute, Data, Router } from '@angular/router';
import { FormsModule, ReactiveFormsModule, UntypedFormArray, UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { TranslocoDirective, TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import { Subject } from 'rxjs';
import { NzNotificationService } from 'ng-zorro-antd/notification';
import { format, formatISO, parseISO } from 'date-fns';
import { getFormErrors } from '@adm-web-app/utils';
import { NotificationService } from '@adm-web-app/services/notification.service';
import { NzAffixComponent } from 'ng-zorro-antd/affix';
import { HxBreadcrumbsComponent } from '@adm-web-app/components/breadcrumbs/breadcrumbs.component';
import { NzPageHeaderComponent, NzPageHeaderTitleDirective } from 'ng-zorro-antd/page-header';
import { NzSkeletonComponent } from 'ng-zorro-antd/skeleton';
import { NzFormControlComponent, NzFormDirective, NzFormItemComponent, NzFormLabelComponent } from 'ng-zorro-antd/form';
import { NzColDirective, NzRowDirective } from 'ng-zorro-antd/grid';
import {
  HxBrandSelectComponent,
  HxCitySelectComponent,
  HxCountrySelectComponent,
  HxEnumSelectComponent,
  HxLabelEditComponent,
  HxOrgSelectComponent
} from 'hx-components';
import { NzInputDirective, NzInputGroupComponent } from 'ng-zorro-antd/input';
import { NzRadioComponent, NzRadioGroupComponent } from 'ng-zorro-antd/radio';
import { NzInputNumberComponent } from 'ng-zorro-antd/input-number';
import { NzDatePickerComponent } from 'ng-zorro-antd/date-picker';
import { NzIconDirective } from 'ng-zorro-antd/icon';

import { NzTooltipDirective } from 'ng-zorro-antd/tooltip';
import { NzDividerComponent } from 'ng-zorro-antd/divider';
import { NgFor, NgIf } from '@angular/common';
import { NzTimePickerComponent } from 'ng-zorro-antd/time-picker';
import { NzAlertComponent } from 'ng-zorro-antd/alert';
import { NzButtonComponent } from 'ng-zorro-antd/button';
import { NzWaveDirective } from 'ng-zorro-antd/core/wave';
import { NzTagComponent } from 'ng-zorro-antd/tag';
import { RouterLink } from '@angular/router';
import { provideTranslocoScope } from '@jsverse/transloco';
import { TreeNodeSelectComponent } from '@adm-web-app/components/tree-node-select/tree-node-select.component';
import { AccessDataService } from '@adm-web-app/modules/access/services/access-data.service';
import { NodeRef, TreeNodeModel } from '@adm-web-app/modules/access/models/access.models';
import { titleIn } from '@adm-web-app/modules/access/util/access-format';

interface FormModel {
  enabled: boolean;
  organizationId: number;
  countryId: number;
  cityId: number;
  address: string;
  longitude: number;
  latitude: number;
  timezone: string;
  title: UiLabel;
  hasOnlineSell: boolean;
  hasDelivery: boolean;
  hasPickup: boolean;
  hasOnlineDelivery: boolean;
  hasDecoration: boolean;
  hasInscription: boolean;
  startWorkDate: Date;
  endWorkDate: Date;
  currency: Currency;
  callCenterPhones: string;
  workTimes: WorkTime[];
  parentId: number | null;
  brandId: number;
  slug: string;
}

interface WorkTime {
  dayOfWeek: number; // 1 - monday
  openTime: Date;
  closeTime: Date;
}

@Component({
    selector: 'hc-store-edit',
    templateUrl: './edit.component.html',
    styleUrls: ['./edit.component.less'],
  providers: [provideTranslocoScope('access')],
  imports: [RouterLink, TreeNodeSelectComponent, NzAffixComponent, HxBreadcrumbsComponent, NzPageHeaderComponent, NzPageHeaderTitleDirective, NzSkeletonComponent, TranslocoDirective, FormsModule, NzFormDirective, ReactiveFormsModule, NzRowDirective, NzColDirective, NzFormItemComponent, NzFormLabelComponent, NzFormControlComponent, HxLabelEditComponent, HxBrandSelectComponent, NzInputDirective, NzRadioGroupComponent, NzRadioComponent, HxCountrySelectComponent, HxCitySelectComponent, HxOrgSelectComponent, NzInputNumberComponent, NzDatePickerComponent, HxEnumSelectComponent, NzIconDirective, NzTooltipDirective, NzDividerComponent, NgFor, NzInputGroupComponent, NzTimePickerComponent, NgIf, NzAlertComponent, NzButtonComponent, NzWaveDirective, NzTagComponent, TranslocoPipe]
})
export class StoreEditComponent implements OnInit, OnDestroy {
  model!: StoreFullModel;
  isEdit = false;
  isCreate = false;
  form!: UntypedFormGroup;

  isLoading = {
    model: false,
  };
  isSaving = false;
  Currency = Currency;
  /** What the org tree says about the store: where it stands, and which points it supplies (read only, they are changed in «Доступы»). */
  placePath = '';
  outOfTree = false;
  outlets: OutletView[] = [];
  parentAllowed: (node: TreeNodeModel) => boolean = () => true;
  private $destroyed = new Subject<void>();

  constructor(
    private aRoute: ActivatedRoute,
    private fb: UntypedFormBuilder,
    private storeService: HxStoreService,
    private tr: TranslocoService,
    private notification: NzNotificationService,
    private router: Router,
    private nt: NotificationService,
    private accessData: AccessDataService,
  ) {
  }

  get workTimes(): UntypedFormGroup[] {
    return (this.form.controls['workTimes'] as UntypedFormArray).controls as UntypedFormGroup[];
  }

  ngOnInit() {
    const routeData: Data = this.aRoute.snapshot.data;
    this.model = routeData['model'] as StoreFullModel;
    if (this.model) {
      this.isEdit = true;
    } else {
      this.model = {enabled: true} as StoreFullModel;
      this.model.workDates = [];
      for (let i = 0; i < 7; i++) {
        this.model.workDates.push({dayOfWeek: i + 1} as StoreWorkTimeModel);
      }
      this.isCreate = true;
    }
    this.form = this.fb.group({
      enabled: [null, [Validators.required]],
      organizationId: [null, [Validators.required]],
      countryId: [null, [Validators.required]],
      cityId: [null, [Validators.required]],
      address: [null, [Validators.required]],
      longitude: [null, [Validators.required]],
      latitude: [null, [Validators.required]],
      timezone: [null, [Validators.required]],
      title: [null, [Validators.required]],
      hasOnlineSell: [null, [Validators.required]],
      hasDelivery: [null, [Validators.required]],
      hasPickup: [null, [Validators.required]],
      hasOnlineDelivery: [null, [Validators.required]],
      hasDecoration: [null, [Validators.required]],
      hasInscription: [null, [Validators.required]],
      startWorkDate: [null, [Validators.required]],
      endWorkDate: [null],
      currency: [null, [Validators.required]],
      callCenterPhones: [null],
      workTimes: this.fb.array([], [Validators.required]),
      parentId: [null, this.isCreate ? [Validators.required] : []],
      brandId: [null, Validators.required],
      slug: [null, Validators.required],
    });
    const workTimesField = this.form.controls['workTimes'] as UntypedFormArray;
    for (let i = 0; i < 7; i++) {
      workTimesField.push(this.fb.group({
        dayOfWeek: [i + 1, Validators.required],
        openTime: [null, [Validators.required]],
        closeTime: [null, [Validators.required]],
      }));
    }
    const formModel: Partial<FormModel> = {
      enabled: this.model.enabled,
      organizationId: this.model.organizationId,
      countryId: this.model.countryId,
      cityId: this.model.city?.id,
      address: this.model.address ?? null,
      longitude: this.model.longitude ?? null,
      latitude: this.model.latitude ?? null,
      timezone: this.model.timezone ?? null,
      title: this.model.title,
      hasOnlineSell: this.model.hasOnlineSell,
      hasDelivery: this.model.hasDelivery,
      hasPickup: this.model.hasPickup,
      hasOnlineDelivery: this.model.hasOnlineDelivery,
      hasDecoration: this.model.hasDecoration,
      hasInscription: this.model.hasInscription,
      startWorkDate: this.model.startWorkDate ? parseISO(this.model.startWorkDate) : undefined,
      endWorkDate: this.model.endWorkDate ? parseISO(this.model.endWorkDate) : undefined,
      currency: this.model.currency,
      callCenterPhones: this.model.callCenterPhones?.join(','),
      workTimes: this.model.workDates.map(wd => ({
        dayOfWeek: wd.dayOfWeek,
        openTime: this.timeToDate(wd.openTime),
        closeTime: this.timeToDate(wd.closeTime)
      } as WorkTime)),
      parentId: null,
      brandId: this.model.brandId,
      slug: this.model.slug ?? null,
    };
    this.form.patchValue(formModel);
    if (this.isEdit) {
      this.form.controls['parentId'].disable();
    }
    void this.loadPlace();
  }

  submitForm() {
    Object.values(this.form.controls).forEach(ctrl => {
      ctrl.markAsDirty();
      ctrl.updateValueAndValidity();
    });
    if (this.form.valid) {
      this.form.disable();
      const formModel = this.form.getRawValue() as FormModel;
      const model: SaveStoreWithPlace = {
        id: this.model.id,
        enabled: formModel.enabled,
        organizationId: formModel.organizationId,
        countryId: formModel.countryId,
        address: formModel.address ?? null,
        cityId: formModel.cityId,
        longitude: formModel.longitude ?? null,
        latitude: formModel.latitude ?? null,
        timezone: formModel.timezone ?? null,
        title: formModel.title,
        hasOnlineSell: formModel.hasOnlineSell,
        hasDelivery: formModel.hasDelivery,
        hasPickup: formModel.hasPickup,
        hasOnlineDelivery: formModel.hasOnlineDelivery,
        hasDecoration: formModel.hasDecoration,
        hasInscription: formModel.hasInscription,
        startDate: formatISO(formModel.startWorkDate, {representation: 'date'}),
        endDate: isoDate(formModel.endWorkDate),
        currency: formModel.currency,
        callCenterPhones: formModel.callCenterPhones?.split(','),
        workTimes: formModel.workTimes.map(wt => ({
          dayOfWeek: wt.dayOfWeek,
          openTime: format(wt.openTime, 'HH:mm'),
          closeTime: format(wt.closeTime, 'HH:mm')
        })),
        parentId: this.isCreate ? formModel.parentId ?? undefined : undefined,
        brandId: formModel.brandId,
        slug: formModel.slug ?? null,
      };
      this.isSaving = true;
      this.storeService.saveStore(model).subscribe(result => {
        this.form.markAsPristine();
        this.notification.success('Магазин', this.tr.translate('nt.dataSaved'));
        if (this.isCreate) {
          this.router.navigate([`/stores/${result.id}/edit`]);
        } else {
          this.isLoading.model = true;
          this.storeService.getFullStore(result.id).then(savedStore => {
            this.model = savedStore;
            this.isLoading.model = false;
          });
        }
        this.form.enable();
        this.isSaving = false;
      }, err => {
        this.nt.handleError(err);
        this.form.enable();
        this.isSaving = false;
      });
    } else {
      console.log('formErrors', getFormErrors(this.form));
    }
  }

  ngOnDestroy(): void {
    this.$destroyed.next();
    this.$destroyed.complete();
  }

  private async loadPlace(): Promise<void> {
    try {
      const tree = await this.accessData.tree();
      const byId = new Map(tree.nodes.map(n => [n.id, n] as const));
      const isPoint = (n: TreeNodeModel): boolean => n.parentId !== null && byId.get(n.parentId)?.kind === 'S';
      // a new store stands under a live group, or under a workshop that is not a point itself
      this.parentAllowed = (n: TreeNodeModel): boolean => n.enabled && !n.removed && (n.kind === 'G' || (n.kind === 'S' && !isPoint(n)));
      if (this.isEdit && this.model.id) {
        const store = byId.get(this.model.id);
        this.outOfTree = !!store && store.parentId === null;
        this.placePath = store ? this.pathOf(store, byId) : '';
        this.outlets = tree.nodes.filter(n => n.parentId === this.model.id && n.kind === 'S' && !n.removed)
          .map(n => ({ id: n.id, title: titleIn(n.title, this.tr.getActiveLang()) }));
        this.form.controls['parentId'].setValue(store?.parentId ?? null);
      }
    } catch {
      this.parentAllowed = () => true;
    }
  }

  private pathOf(node: TreeNodeModel, byId: ReadonlyMap<number, TreeNodeModel>): string {
    const lang = this.tr.getActiveLang();
    const refs: NodeRef[] = [];
    let current: TreeNodeModel | undefined = node.parentId === null ? undefined : byId.get(node.parentId);
    const seen = new Set<number>();
    while (current && !seen.has(current.id)) {
      seen.add(current.id);
      refs.unshift({ id: current.id, title: current.title, kind: current.kind });
      current = current.parentId === null ? undefined : byId.get(current.parentId);
    }
    return refs.map(r => titleIn(r.title, lang)).join(' › ');
  }

  private timeToDate(time?: string): Date | undefined {
    if (!time) {
      return undefined;
    }
    const [hours, minutes] = time.split(':').map(i => Number(i));
    const d = new Date();
    d.setHours(hours, minutes);
    return d;
  }
}
